/**
 * Strategy Canvas Editor
 *
 * Graphical, one-page strategy communication framework.
 * Inline-editable components for Vision, Purpose, Mission, Guiding Principles,
 * Competencies, and Assumptions. Right panel shows linked Strategies (with
 * CRUD / link ops) and Outcome Drivers pulled live from their stores.
 *
 * Auto-saves on blur.
 *
 * Route: /prioritization/strategy/canvas/:canvasId
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  LayoutTemplate,
  ChevronLeft,
  Check,
  Loader2,
  AlertCircle,
  PenLine,
  Target,
  TreeDeciduous,
  Flag,
  Crosshair,
  BarChart3,
  Plus,
  X,
  Search,
  Network,
  LayoutGrid,
  Printer,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from './StrategyConnectivityDocModal';
import {
  useStrategyCanvasStore,
  type StrategyCanvas,
  type UpdateCanvasInput,
} from '../../../stores/prioritization/strategyCanvasStore';
import { useOutcomeDriverStore } from '../../../stores/prioritization/outcomeDriverStore';
import { useStrategyStore } from '../../../stores/prioritization/strategyStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { Strategy } from '../../../types/prioritization/strategy';
import {
  listGoals,
  listObjectives,
  createGoal,
  type Goal,
  type StrategicObjective,
} from '../../../lib/strategyAlignment';
import { createStrategicObjective as createObjective } from '../../../lib/strategy/createStrategyFlow';
import {
  buildPreviewForNewStrategy,
  buildPreviewForObjectiveOrGoal,
  buildPreviewForOutcomeDriver,
} from './strategyCanvasCreatePreview';
import {
  StrategyCanvasCreateConfirmProvider,
  useStrategyCanvasCreateConfirmOptional,
} from './StrategyCanvasCreateConfirm';
import {
  StrategyCanvasUnlinkConfirmModal,
  type StrategyCanvasUnlinkImpact,
} from './StrategyCanvasUnlinkConfirm';

type StrategyUnlinkModalState =
  | { open: false }
  | { open: true; loading: true; strategyId: string; strategyName: string }
  | {
      open: true;
      loading: false;
      strategyId: string;
      strategyName: string;
      impact: StrategyCanvasUnlinkImpact;
    };

interface BlockDef {
  key: keyof UpdateCanvasInput;
  label: string;
  description: string;
  placeholder: string;
  accent: string;
  accentText: string;
  accentBorder: string;
  accentBg: string;
}

/** Objectives/goals shown on the canvas: unassigned when no strategies linked; otherwise linked strategies + unassigned. */
function tacticItemBelongsOnCanvas(
  item: Goal | StrategicObjective,
  linkedStrategyIds: string[],
): boolean {
  const sid = (item as Goal).strategyId;
  if (!linkedStrategyIds.length) {
    return sid == null || sid === '';
  }
  return sid == null || sid === '' || linkedStrategyIds.includes(sid);
}

// ── Component definitions ──────────────────────────────────────

const BLOCKS: BlockDef[] = [
  {
    key: 'vision',
    label: 'Vision',
    description: 'Where are we going? The aspirational future state the organization is working toward.',
    placeholder: 'Describe the aspirational future state...',
    accent: 'text-[var(--text-sec)]',
    accentText: 'text-[var(--text-sec)]',
    accentBorder: 'border-[var(--slate)]',
    accentBg: 'bg-[var(--slate-dim)]',
  },
  {
    key: 'purpose',
    label: 'Purpose',
    description: 'Why do we exist? The fundamental reason the organization exists beyond profit.',
    placeholder: 'Describe why the organization exists...',
    accent: 'text-[var(--text-sec)]',
    accentText: 'text-[var(--text-sec)]',
    accentBorder: 'border-[var(--slate)]',
    accentBg: 'bg-[var(--slate-dim)]',
  },
  {
    key: 'mission',
    label: 'Mission',
    description: 'What do we do? The specific work the organization does to achieve its vision.',
    placeholder: 'Describe what the organization does...',
    accent: 'text-[var(--text-sec)]',
    accentText: 'text-[var(--text-sec)]',
    accentBorder: 'border-[var(--slate)]',
    accentBg: 'bg-[var(--slate-dim)]',
  },
  {
    key: 'guidingPrinciples',
    label: 'Guiding Principles',
    description: 'How do we behave? The values and behaviors that govern how the organization operates.',
    placeholder: 'List the guiding principles...',
    accent: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    accentText: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    accentBorder: 'border-[var(--green)]',
    accentBg: 'bg-[var(--green-dim)]',
  },
  {
    key: 'competencies',
    label: 'Core Competencies',
    description: 'What are we uniquely good at? The capabilities that differentiate the organization.',
    placeholder: 'Describe the core competencies...',
    accent: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    accentText: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    accentBorder: 'border-[var(--green)]',
    accentBg: 'bg-[var(--green-dim)]',
  },
  {
    key: 'assumptions',
    label: 'Assumptions',
    description: 'What are we taking for granted? Key beliefs about the environment that shape the strategy.',
    placeholder: 'List the strategic assumptions...',
    accent: 'text-[var(--mustard)]',
    accentText: 'text-[var(--mustard)]',
    accentBorder: 'border-[var(--mustard)]',
    accentBg: 'bg-[var(--mustard-dim)]',
  },
];

// ── Inline editable component ──────────────────────────────────

function CanvasBlock({
  block,
  value,
  onSave,
  saving,
}: {
  block: BlockDef;
  value: string | null;
  onSave: (key: keyof UpdateCanvasInput, val: string) => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  function startEdit() {
    setEditing(true);
    setDraft(value ?? '');
    setTimeout(() => textareaRef.current?.focus(), 50);
  }

  function commit() {
    setEditing(false);
    if (draft !== (value ?? '')) {
      onSave(block.key, draft);
    }
  }

  const isEmpty = !value?.trim();

  return (
    <div
      className={`relative  border-2 transition-all ${
        editing
          ? `${block.accentBorder} shadow-sm`
          : `border-[var(--rule)] hover:${block.accentBorder} hover:shadow-sm`
      } ${block.accentBg} dark:bg-opacity-10 overflow-hidden`}
    >
      {/* Header strip */}
      <div className={`flex items-center justify-between px-4 py-2.5 border-b ${block.accentBorder}`}>
        <div>
          <span className={`text-xs font-semibold uppercase tracking-wider ${block.accent}`}>
            {block.label}
          </span>
          <p className="text-xs text-[var(--text-ter)] mt-0.5 leading-snug">{block.description}</p>
        </div>
        {!editing && (
          <button
            onClick={startEdit}
            className={`p-1.5  ${block.accentBg} ${block.accent} hover:opacity-80 transition-opacity flex-shrink-0 ml-2`}
            title={`Edit ${block.label}`}
          >
            <PenLine className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="px-4 py-4 min-h-[80px]">
        {editing ? (
          <div className="space-y-2">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setEditing(false); setDraft(value ?? ''); }
              }}
              rows={4}
              className="w-full resize-none bg-[var(--bg-raised)] text-sm text-[var(--text-pri)] border border-[var(--rule)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={block.placeholder}
            />
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => { setEditing(false); setDraft(value ?? ''); }}
                className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] px-2 py-1 rounded hover:bg-[var(--hover-tint)]"
              >
                Cancel
              </button>
              <button
                onClick={commit}
                disabled={saving}
                className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1  text-white transition-colors ${
                  saving ? 'bg-[var(--surface)]' : `bg-[var(--slate)] hover:bg-[var(--slate)]`
                }`}
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Save
              </button>
            </div>
          </div>
        ) : isEmpty ? (
          <button
            onClick={startEdit}
            className="w-full text-left text-sm text-[var(--text-ter)] italic hover:text-[var(--text-sec)]"
          >
            {block.placeholder}
          </button>
        ) : (
          <button
            onClick={startEdit}
            className="w-full text-left text-sm text-[var(--text-sec)] whitespace-pre-wrap leading-relaxed hover:text-[var(--text-pri)]"
          >
            {value}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Shared: select / create / link strategies (components panel + diagram) ──

/** Layout for compact + control to reveal search/create on the strategy canvas */
const CANVAS_ADD_ICON_BTN_LAYOUT =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center  border shadow-sm transition-colors';

type StrategyLinkPickerVariant = 'panel' | 'diagram';

function StrategyLinkPicker({
  canvasTitle,
  linkedStrategyIds,
  allStrategies,
  onLink,
  onUnlink,
  userId,
  workspaceId,
  variant,
}: {
  canvasTitle: string;
  linkedStrategyIds: string[];
  allStrategies: Strategy[];
  onLink: (strategyId: string) => Promise<void> | void;
  onUnlink: (strategyId: string) => void;
  userId: string;
  workspaceId: string;
  variant: StrategyLinkPickerVariant;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [linking, setLinking] = useState<string | null>(null);
  const [creatingStrategy, setCreatingStrategy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const addFormRef = useRef<HTMLInputElement>(null);
  const fetchStrategies = useStrategyStore((s) => s.fetchFromSupabase);
  // The one strategy writer (strategyStore.createStrategy) — the same path as
  // the Strategies list and Strategy detail pages.
  const createStrategy = useStrategyStore((s) => s.createStrategy);
  const createConfirm = useStrategyCanvasCreateConfirmOptional();

  useEffect(() => {
    if (addOpen) setTimeout(() => addFormRef.current?.focus(), 50);
  }, [addOpen]);

  const isDiagram = variant === 'diagram';
  const textItem = isDiagram ? 'text-sm' : 'text-xs';
  const textMeta = isDiagram ? 'text-xs' : 'text-[10px]';
  const inputPad = isDiagram ? 'py-2 pl-9 pr-3' : 'py-1.5 pl-8 pr-3';
  const inputText = isDiagram ? 'text-sm' : 'text-xs';
  const iconSearch = isDiagram ? 'h-4 w-4 left-3' : 'h-3.5 w-3.5 left-2.5';

  const linkedSet = useMemo(() => new Set(linkedStrategyIds), [linkedStrategyIds]);

  const linkedStrategies = useMemo(
    () => allStrategies.filter((s) => linkedSet.has(s.id)),
    [allStrategies, linkedSet],
  );

  const suggestions = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    return allStrategies
      .filter((s) => !linkedSet.has(s.id) && s.name?.toLowerCase().includes(q))
      .slice(0, 6);
  }, [allStrategies, linkedSet, query]);

  async function handleLink(strategyId: string) {
    setLinking(strategyId);
    await onLink(strategyId);
    setQuery('');
    setLinking(null);
    setAddOpen(false);
    setCreateError(null);
  }

  async function handleCreateAndLink() {
    const name = query.trim();
    if (!name || !userId || !workspaceId || creatingStrategy) return;
    setCreateError(null);

    if (createConfirm) {
      const decision = await createConfirm.requestConfirm({
        title: 'Create strategy',
        summary: `You are creating "${name}" on this canvas. The list below is everything that comes with that action: the new strategy, its link to this canvas, and how it sits alongside strategies already here.`,
        rows: buildPreviewForNewStrategy({
          newStrategyName: name,
          canvasTitle,
          otherLinkedStrategyNames: linkedStrategies.map((s) => s.name),
        }),
      });
      if (decision !== 'proceed') return;
    }

    setCreatingStrategy(true);
    try {
      const created = await createStrategy({
        name,
        description: '',
        timeHorizon: 'annual',
        ownerId: userId,
        stakeholderIds: [],
        workspaceId,
      });
      if (!created.ok || !created.id) {
        throw new Error(created.error || 'Could not create strategy');
      }
      await onLink(created.id);
      setQuery('');
      void fetchStrategies();
      setAddOpen(false);
    } catch (e) {
      console.error('[StrategyLinkPicker] createStrategy:', e);
      setCreateError(e instanceof Error ? e.message : 'Could not create strategy');
    }
    setCreatingStrategy(false);
  }

  const createBtnClass = `flex items-center gap-1 font-medium shrink-0 text-xs ${
    creatingStrategy || !query.trim() || !userId
      ? 'text-[var(--text-ter)] cursor-not-allowed'
      : 'text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)]'
  }`;

  const diagramBody = (
    <>
      {linkedStrategies.length > 0 ? (
        <ul className="space-y-1 mb-3 max-h-56 overflow-y-auto pr-1">
          {linkedStrategies.map((s) => (
            <li key={s.id} className="flex items-start gap-2">
              <Flag className="h-4 w-4 mt-0.5 text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-shrink-0" />
              <div className="flex-1 min-w-0 flex items-start gap-2">
                <Link
                  to={`/prioritization/strategy/${s.id}`}
                  className="flex-1 min-w-0 group"
                >
                  <p className="text-sm font-medium text-[var(--text-pri)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)] truncate leading-snug">
                    {s.name}
                  </p>
                  <p className="text-xs text-[var(--text-ter)] mt-0.5">
                    {s.timeHorizon} · {s.status}
                  </p>
                </Link>
                <button
                  type="button"
                  onClick={() => onUnlink(s.id)}
                  className="flex-shrink-0 p-1 rounded text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                  title="Remove from canvas"
                  aria-label="Remove strategy from canvas"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-[var(--text-ter)] mb-3 italic">
          No strategies linked yet. Click + to search or create one.
        </p>
      )}

      {!addOpen ? (
        <button
          type="button"
          onClick={() => {
            setAddOpen(true);
            setCreateError(null);
          }}
          className={`${CANVAS_ADD_ICON_BTN_LAYOUT} border-[var(--slate)]/80 bg-[var(--bg-raised)] text-[var(--text-ter)] hover:bg-[var(--slate-dim)] hover:text-[var(--text-ter)] dark:border-[var(--slate)]/50 dark:text-[var(--text-ter)] dark:hover:bg-[var(--slate)]/50 dark:hover:text-[var(--text-ter)]`}
          title="Add strategy"
          aria-label="Add strategy"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-start">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
              <input
                ref={addFormRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setCreateError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (suggestions.length > 0) void handleLink(suggestions[0].id);
                  else if (query.trim()) void handleCreateAndLink();
                }}
                placeholder="Search strategies or type a new name…"
                className="w-full py-2 pl-10 pr-3 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-2 focus:ring-violet-500/60"
              />
            </div>
            <button
              type="button"
              disabled={creatingStrategy || !query.trim() || !userId}
              onClick={() => void handleCreateAndLink()}
              className="inline-flex items-center justify-center gap-1 border border-[var(--slate)] dark:border-[var(--slate)]/60 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 px-3 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/35 disabled:opacity-40 disabled:pointer-events-none whitespace-nowrap shrink-0"
              title="Create a strategy from the search text and link it to this canvas"
            >
              <Plus className="h-4 w-4 shrink-0" />
              {creatingStrategy ? 'Adding…' : 'Create strategy'}
            </button>
          </div>

          {createError && (
            <p className="mt-2 text-sm text-[var(--coral)] dark:text-[var(--coral)]">{createError}</p>
          )}

          {query.trim() && suggestions.length === 0 && !creatingStrategy && (
            <p className="mt-2 text-xs text-[var(--text-sec)]">
              No match. Press Enter or Create strategy to add &quot;{query.trim()}&quot; to this workspace and canvas.
            </p>
          )}

          {suggestions.length > 0 && (
            <div className="mt-2 border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] shadow-sm max-h-48 overflow-y-auto z-30 relative">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => void handleLink(s.id)}
                  disabled={linking === s.id}
                  className="w-full text-left px-3 py-2.5 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 transition-colors disabled:opacity-50"
                >
                  <p className="text-sm font-medium text-[var(--text-pri)] truncate">{s.name}</p>
                  <p className="text-xs text-[var(--text-ter)] mt-0.5">
                    {s.timeHorizon} · {s.status}
                  </p>
                </button>
              ))}
            </div>
          )}

          {!query.trim() && (
            <p className="mt-2 text-sm text-[var(--text-sec)] leading-relaxed">
              Type a name to find a strategy, press Enter to link the first match, or use Create strategy.
            </p>
          )}

          <button
            type="button"
            onClick={() => {
              setAddOpen(false);
              setQuery('');
              setCreateError(null);
            }}
            className="mt-2 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
          >
            Cancel
          </button>
        </>
      )}

      <p className="mt-3 pt-2 border-t border-[var(--rule)] dark:border-[var(--rule)]/80">
        <Link
          to="/prioritization/strategy/list"
          className="text-[10px] text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
        >
          All strategies
        </Link>
      </p>
    </>
  );

  if (isDiagram) {
    return (
      <div className="border border-[var(--slate)]/70 dark:border-[var(--slate)]/45 bg-[var(--bg-raised)] p-4 shadow-sm">
        {diagramBody}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)]">
        <div className="flex items-center gap-1.5">
          <Flag className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-sec)]">
            Strategies
          </span>
        </div>
        {addOpen && (
          <button
            type="button"
            disabled={creatingStrategy || !query.trim() || !userId}
            onClick={() => void handleCreateAndLink()}
            className={createBtnClass}
            title="Creates a strategy from the search text and links it to this canvas"
          >
            <Plus className="h-3.5 w-3.5" />
            {creatingStrategy ? 'Adding…' : 'Create & link'}
          </button>
        )}
        {!addOpen && (
          <button
            type="button"
            onClick={() => {
              setAddOpen(true);
              setCreateError(null);
            }}
            className={`${CANVAS_ADD_ICON_BTN_LAYOUT} border-[var(--rule)]/90 bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)] hover:text-[var(--text-pri)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-white`}
            title="Add strategy"
            aria-label="Add strategy"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
          </button>
        )}
      </div>

      {linkedStrategies.length === 0 && !addOpen && (
        <p className="px-3 py-2.5 text-xs text-[var(--text-ter)] italic">
          No strategies linked yet. Click + in the header to search or create.
        </p>
      )}

      {linkedStrategies.length > 0 && (
        <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {linkedStrategies.map((s) => (
            <div key={s.id} className="flex items-start gap-2 px-3 py-2.5">
              <Link
                to={`/prioritization/strategy/${s.id}`}
                className="flex-1 min-w-0 group"
              >
                <p className={`${textItem} font-medium text-[var(--text-pri)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)] truncate leading-snug`}>
                  {s.name}
                </p>
                <p className={`${textMeta} text-[var(--text-ter)] mt-0.5`}>
                  {s.timeHorizon} · {s.status}
                </p>
              </Link>
              <button
                type="button"
                onClick={() => onUnlink(s.id)}
                className="flex-shrink-0 p-1 rounded text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                title="Remove from canvas"
                aria-label="Remove strategy from canvas"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {addOpen && (
        <div className="px-3 py-3 border-t border-[var(--rule)]">
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 text-[var(--text-ter)] ${iconSearch}`} />
            <input
              ref={addFormRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setCreateError(null);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (suggestions.length > 0) void handleLink(suggestions[0].id);
                else if (query.trim()) void handleCreateAndLink();
              }}
              placeholder="Search, link, or type a new name…"
              className={`w-full ${inputPad} ${inputText} border border-[var(--rule)]  bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-2 focus:ring-indigo-400`}
            />
          </div>

          {createError && (
            <p className="mt-2 text-[var(--coral)] dark:text-[var(--coral)] px-1 text-xs">{createError}</p>
          )}

          {query.trim() && suggestions.length === 0 && !creatingStrategy && (
            <p className="mt-2 text-[var(--text-sec)] px-1 text-xs">
              No match. Press Enter or use Create & link to add &quot;{query.trim()}&quot; to this workspace and canvas.
            </p>
          )}

          {suggestions.length > 0 && (
            <div className="mt-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] shadow-sm max-h-48 overflow-y-auto z-30 relative">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => void handleLink(s.id)}
                  disabled={linking === s.id}
                  className="w-full text-left px-3 py-2.5 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 transition-colors disabled:opacity-50"
                >
                  <p className={`${textItem} font-medium text-[var(--text-pri)] truncate`}>{s.name}</p>
                  <p className={`${textMeta} text-[var(--text-ter)] mt-0.5`}>
                    {s.timeHorizon} · {s.status}
                  </p>
                </button>
              ))}
            </div>
          )}

          {!query.trim() && (
            <p className="mt-2 text-[var(--text-sec)] text-xs leading-relaxed">
              Type a name to find a strategy, press Enter to link the first match, or use Create & link.
            </p>
          )}

          <button
            type="button"
            onClick={() => {
              setAddOpen(false);
              setQuery('');
              setCreateError(null);
            }}
            className="mt-2 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="px-3 pb-3">
        <Link
          to="/prioritization/strategy/list"
          className="text-[10px] text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
        >
          All strategies
        </Link>
      </div>
    </>
  );
}

function LinkedStrategiesPanel(props: {
  canvasTitle: string;
  linkedStrategyIds: string[];
  allStrategies: Strategy[];
  onLink: (strategyId: string) => Promise<void> | void;
  onUnlink: (strategyId: string) => void;
  userId: string;
  workspaceId: string;
}) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
      <StrategyLinkPicker {...props} variant="panel" />
    </div>
  );
}

// ── Right panel: Outcome Drivers ──────────────────────────────

function DriversPanel({
  canvasTitle,
  linkedStrategyIds,
  allStrategies,
}: {
  canvasTitle: string;
  linkedStrategyIds: string[];
  allStrategies: Strategy[];
}) {
  const drivers = useOutcomeDriverStore((s) => s.drivers);
  const createDriver = useOutcomeDriverStore((s) => s.createDriver);
  const fetchFromSupabase = useOutcomeDriverStore((s) => s.fetchFromSupabase);
  const [addOpen, setAddOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [linkDriverToStrategy, setLinkDriverToStrategy] = useState(true);
  const [adding, setAdding] = useState(false);
  const [driverError, setDriverError] = useState<string | null>(null);
  const createConfirm = useStrategyCanvasCreateConfirmOptional();
  const addFormRef = useRef<HTMLInputElement>(null);

  const primaryStrategyId = linkedStrategyIds[0];
  const primaryStrategyName = useMemo(() => {
    if (!primaryStrategyId) return null;
    return allStrategies.find((s) => s.id === primaryStrategyId)?.name ?? null;
  }, [allStrategies, primaryStrategyId]);

  useEffect(() => {
    fetchFromSupabase();
  }, [fetchFromSupabase]);

  useEffect(() => {
    if (addOpen) {
      setLinkDriverToStrategy(Boolean(primaryStrategyId));
      setTimeout(() => addFormRef.current?.focus(), 50);
    }
  }, [addOpen, primaryStrategyId]);

  async function handleAddDriver() {
    const title = titleDraft.trim();
    if (!title || adding) return;
    setDriverError(null);

    if (createConfirm) {
      const decision = await createConfirm.requestConfirm({
        title: 'Create outcome driver',
        summary: `You are creating "${title}". Choose below whether to attach it to a strategy on this canvas; the list reflects your choice.`,
        rows: buildPreviewForOutcomeDriver({
          title,
          canvasTitle,
          linkToPrimaryStrategy: linkDriverToStrategy && Boolean(primaryStrategyId),
          primaryStrategyName,
          hasStrategiesOnCanvas: linkedStrategyIds.length > 0,
          linkedStrategyNamesOnCanvas: allStrategies
            .filter((s) => linkedStrategyIds.includes(s.id))
            .map((s) => s.name),
        }),
      });
      if (decision !== 'proceed') return;
    }

    setAdding(true);
    const { error } = await createDriver({
      title,
      contextOfPurpose: '',
      currentState: '',
      problemOpportunity: '',
      ramifications: '',
      narrative: '',
      strategyId: linkDriverToStrategy && primaryStrategyId ? primaryStrategyId : null,
    });
    setAdding(false);
    if (error) {
      setDriverError(error);
      return;
    }
    setTitleDraft('');
    setAddOpen(false);
  }

  return (
    <div className="space-y-3">
      {drivers.length > 0 && (
        <ul className="space-y-2">
          {drivers.map((d) => (
            <li key={d.id}>
              <Link
                to="/prioritization/outcome-drivers"
                className="flex items-start gap-2 group"
              >
                <TreeDeciduous className="h-3.5 w-3.5 mt-0.5 text-[var(--green)] flex-shrink-0" />
                <span className="text-xs text-[var(--text-sec)] group-hover:text-[var(--text-ter)] leading-snug">
                  {d.title || 'Untitled driver'}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {drivers.length === 0 && (
        <p className="text-xs text-[var(--text-ter)] italic">No outcome drivers yet. Click + to add one.</p>
      )}

      {!addOpen ? (
        <button
          type="button"
          onClick={() => {
            setAddOpen(true);
            setDriverError(null);
          }}
          className={`${CANVAS_ADD_ICON_BTN_LAYOUT} border-[var(--green)]/80 bg-[var(--bg-raised)] text-[var(--green)] hover:bg-[var(--green-dim)] hover:text-[var(--green)] dark:border-[var(--green)]/45 dark:text-[var(--text-ter)] dark:hover:bg-[var(--green)]/40 dark:hover:text-[var(--text-ter)]`}
          title="Add outcome driver"
          aria-label="Add outcome driver"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="flex gap-1.5">
            <input
              ref={addFormRef}
              type="text"
              value={titleDraft}
              onChange={(e) => { setTitleDraft(e.target.value); setDriverError(null); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleAddDriver();
                }
              }}
              placeholder="New outcome driver title…"
              disabled={adding}
              className="flex-1 min-w-0 text-xs border border-[var(--rule)] px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)]/40 text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => void handleAddDriver()}
              disabled={adding || !titleDraft.trim()}
              className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:pointer-events-none"
            >
              {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Add
            </button>
          </div>
          {primaryStrategyId && primaryStrategyName ? (
            <label className="flex items-start gap-2 cursor-pointer text-xs text-[var(--text-sec)] leading-snug">
              <input
                type="checkbox"
                checked={linkDriverToStrategy}
                onChange={(e) => setLinkDriverToStrategy(e.target.checked)}
                className="mt-0.5 rounded border-[var(--rule)] text-[var(--green)] dark:text-[var(--text-ter)] focus:ring-emerald-500"
              />
              <span>
                Link to{' '}
                <span className="font-medium text-[var(--text-pri)]">{primaryStrategyName}</span>
                {' '}on this canvas
              </span>
            </label>
          ) : null}
          {driverError && <p className="text-xs text-[var(--coral)]">{driverError}</p>}
          <button
            type="button"
            onClick={() => {
              setAddOpen(false);
              setTitleDraft('');
              setDriverError(null);
            }}
            className="self-start text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/** Outcome drivers on the diagram: list, search existing, create new (matches strategy/objectives pattern). */
function DiagramOutcomeDriversPicker({
  linkedStrategyIds,
  canvasTitle,
  primaryStrategyName,
  linkedStrategyNamesOnCanvas,
}: {
  linkedStrategyIds: string[];
  canvasTitle: string;
  primaryStrategyName: string | null;
  linkedStrategyNamesOnCanvas: string[];
}) {
  const drivers = useOutcomeDriverStore((s) => s.drivers);
  const createDriver = useOutcomeDriverStore((s) => s.createDriver);
  const fetchFromSupabase = useOutcomeDriverStore((s) => s.fetchFromSupabase);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [linkDriverToStrategy, setLinkDriverToStrategy] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createConfirm = useStrategyCanvasCreateConfirmOptional();
  const addFormRef = useRef<HTMLInputElement>(null);

  const primaryStrategyId = linkedStrategyIds[0];

  useEffect(() => {
    void fetchFromSupabase();
  }, [fetchFromSupabase]);

  useEffect(() => {
    if (addOpen) {
      setLinkDriverToStrategy(Boolean(primaryStrategyId));
      setTimeout(() => addFormRef.current?.focus(), 50);
    }
  }, [addOpen, primaryStrategyId]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return drivers.filter((d) => (d.title || '').toLowerCase().includes(q)).slice(0, 6);
  }, [drivers, query]);

  function titleExists(title: string): boolean {
    const t = title.trim().toLowerCase();
    return drivers.some((d) => (d.title || '').trim().toLowerCase() === t);
  }

  async function handleCreate() {
    const title = query.trim();
    if (!title || adding) return;
    if (titleExists(title)) {
      setError('A driver with this title already exists.');
      return;
    }
    if (createConfirm) {
      const decision = await createConfirm.requestConfirm({
        title: 'Create outcome driver',
        summary: `You are creating "${title}". Choose below whether to attach it to a strategy on this canvas; the list reflects your choice.`,
        rows: buildPreviewForOutcomeDriver({
          title,
          canvasTitle,
          linkToPrimaryStrategy: linkDriverToStrategy && Boolean(primaryStrategyId),
          primaryStrategyName,
          hasStrategiesOnCanvas: linkedStrategyIds.length > 0,
          linkedStrategyNamesOnCanvas,
        }),
      });
      if (decision !== 'proceed') return;
    }
    setError(null);
    setAdding(true);
    const { error: createErr } = await createDriver({
      title,
      contextOfPurpose: '',
      currentState: '',
      problemOpportunity: '',
      ramifications: '',
      narrative: '',
      strategyId: linkDriverToStrategy && primaryStrategyId ? primaryStrategyId : null,
    });
    setAdding(false);
    if (createErr) {
      setError(createErr);
      return;
    }
    setQuery('');
    void fetchFromSupabase();
    setAddOpen(false);
  }

  return (
    <div className="border border-[var(--green)]/70 dark:border-[var(--green)]/50 bg-[var(--bg-raised)] p-4 shadow-sm mb-3">
      {drivers.length > 0 ? (
        <ul className="space-y-1 mb-3 max-h-56 overflow-y-auto pr-1">
          {drivers.slice(0, 16).map((d) => (
            <li key={d.id}>
              <Link
                to="/prioritization/outcome-drivers"
                className="flex items-start gap-2 group text-sm text-[var(--text-pri)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]"
              >
                <TreeDeciduous className="h-4 w-4 mt-0.5 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0" />
                <span className="leading-snug">{d.title || 'Untitled driver'}</span>
              </Link>
            </li>
          ))}
          {drivers.length > 16 && (
            <li className="text-xs text-[var(--text-ter)] pl-9">+{drivers.length - 16} more</li>
          )}
        </ul>
      ) : (
        <p className="text-sm text-[var(--text-ter)] mb-3 italic">
          No outcome drivers yet. Click + to search or create one.
        </p>
      )}

      {!addOpen ? (
        <button
          type="button"
          onClick={() => {
            setAddOpen(true);
            setError(null);
          }}
          className={`${CANVAS_ADD_ICON_BTN_LAYOUT} border-[var(--green)]/80 bg-[var(--bg-raised)] text-[var(--green)] hover:bg-[var(--green-dim)] hover:text-[var(--green)] dark:border-[var(--green)]/45 dark:text-[var(--text-ter)] dark:hover:bg-[var(--green)]/40 dark:hover:text-[var(--text-ter)]`}
          title="Add outcome driver"
          aria-label="Add outcome driver"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-start">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
              <input
                ref={addFormRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  void handleCreate();
                }}
                placeholder="Search drivers or type a new title…"
                disabled={adding}
                className="w-full py-2 pl-10 pr-3 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-2 focus:ring-emerald-500/60 disabled:opacity-50"
              />
            </div>
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={adding || !query.trim()}
              className="inline-flex items-center justify-center gap-1 border border-[var(--green)] dark:border-[var(--green)]/60 bg-[var(--green-dim)] dark:bg-[var(--green)]/40 px-3 py-2 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/30 disabled:opacity-40 disabled:pointer-events-none whitespace-nowrap shrink-0"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {adding ? 'Adding…' : 'Create driver'}
            </button>
          </div>

          {primaryStrategyId && primaryStrategyName ? (
            <label className="mt-2 flex items-start gap-2 cursor-pointer text-sm text-[var(--text-sec)] leading-snug">
              <input
                type="checkbox"
                checked={linkDriverToStrategy}
                onChange={(e) => setLinkDriverToStrategy(e.target.checked)}
                className="mt-1 rounded border-[var(--rule)] text-[var(--green)] dark:text-[var(--text-ter)] focus:ring-emerald-500"
              />
              <span>
                Link to{' '}
                <span className="font-medium text-[var(--text-pri)]">{primaryStrategyName}</span>
                {' '}on this canvas
              </span>
            </label>
          ) : null}

          {error && <p className="mt-2 text-sm text-[var(--coral)] dark:text-[var(--coral)]">{error}</p>}

          {query.trim() && suggestions.length > 0 && (
            <div className="mt-2 border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] shadow-sm max-h-40 overflow-y-auto">
              {suggestions.map((d) => (
                <Link
                  key={d.id}
                  to="/prioritization/outcome-drivers"
                  className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/30 text-sm text-[var(--text-pri)]"
                >
                  <TreeDeciduous className="h-3.5 w-3.5 text-[var(--green)] shrink-0" />
                  <span className="truncate">{d.title || 'Untitled'}</span>
                </Link>
              ))}
            </div>
          )}

          {query.trim() && suggestions.length === 0 && !adding && !titleExists(query) && (
            <p className="mt-2 text-xs text-[var(--text-sec)]">
              Press Enter or Create driver to add &quot;{query.trim()}&quot;
              {primaryStrategyId
                ? '—use the checkbox above to link it to your first strategy on this canvas or leave it independent.'
                : '.'}
            </p>
          )}

          <button
            type="button"
            onClick={() => {
              setAddOpen(false);
              setQuery('');
              setError(null);
            }}
            className="mt-2 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
          >
            Cancel
          </button>
        </>
      )}

      <p className="mt-3 pt-2 border-t border-[var(--rule)] dark:border-[var(--rule)]/80">
        <Link
          to="/prioritization/outcome-drivers"
          className="text-[10px] text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
        >
          All outcome drivers
        </Link>
      </p>
    </div>
  );
}

// ── Diagram view (one-page strategy canvas grid) ──────

function DownArrowRow({ weights = [1, 1, 1] }: { weights?: number[] }) {
  return (
    <div className="flex border-y border-[var(--rule)] bg-[var(--surface)]" style={{ height: 26 }}>
      {weights.map((w, i) => (
        <div key={i} style={{ flex: w }} className="flex items-center justify-center">
          <svg viewBox="0 0 20 13" width="18" height="13">
            <polygon points="0,0 20,0 10,13" fill="#d1d5db" />
          </svg>
        </div>
      ))}
    </div>
  );
}

function BidirArrow() {
  return (
    <svg viewBox="0 0 30 16" width="30" height="16">
      <polygon points="0,8 8,2 8,6 22,6 22,2 30,8 22,14 22,10 8,10 8,14" fill="#d1d5db" />
    </svg>
  );
}

function CanvasCellLabel({ text }: { text: string }) {
  return (
    <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[var(--text-sec)] mb-2 leading-tight">
      {text}
    </div>
  );
}

// ── Inline-editable diagram cell ──────────────────────────────

function DiagramEditableCell({
  fieldKey,
  value,
  placeholder,
  onSave,
}: {
  fieldKey: keyof UpdateCanvasInput;
  value: string | null;
  placeholder: string;
  onSave: (key: keyof UpdateCanvasInput, val: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  function startEdit() {
    setDraft(value ?? '');
    setEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }

  function commit() {
    setEditing(false);
    if (draft !== (value ?? '')) onSave(fieldKey, draft);
  }

  return (
    <div
      className="group relative"
      onClick={!editing ? startEdit : undefined}
    >
      {editing ? (
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setEditing(false); setDraft(value ?? ''); }
          }}
          rows={5}
          className="w-full resize-none bg-[var(--bg-raised)] text-sm text-[var(--text-pri)] border border-[var(--slate)]/80 dark:border-[var(--slate)]/50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 shadow-sm"
          placeholder={placeholder}
        />
      ) : value?.trim() ? (
        <>
          <p className="text-sm text-[var(--text-pri)] leading-relaxed whitespace-pre-wrap cursor-pointer">
            {value}
          </p>
          <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-50 transition-opacity pointer-events-none">
            <PenLine className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-[var(--text-ter)] italic cursor-pointer group-hover:text-[var(--text-sec)] dark:group-hover:text-[var(--text-ter)] transition-colors">
            {placeholder}
          </p>
          <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-50 transition-opacity pointer-events-none">
            <PenLine className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          </div>
        </>
      )}
    </div>
  );
}

// ── Tactics sub-cell (Goals or Objectives) ────────────────────

function TacticsSubCell({
  type,
  variant = 'panel',
  linkedStrategyIds,
  workspaceId,
  userId,
  canvasTitle,
  primaryStrategyName,
  linkedStrategyNamesOnCanvas,
}: {
  type: 'goals' | 'objectives';
  variant?: 'panel' | 'diagram';
  linkedStrategyIds: string[];
  workspaceId: string;
  userId: string;
  canvasTitle: string;
  primaryStrategyName: string | null;
  linkedStrategyNamesOnCanvas: string[];
}) {
  const [items, setItems] = useState<Array<Goal | StrategicObjective>>([]);
  const [allItems, setAllItems] = useState<Array<Goal | StrategicObjective>>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const createConfirm = useStrategyCanvasCreateConfirmOptional();
  const addFormRef = useRef<HTMLInputElement>(null);

  const primaryStrategyId = linkedStrategyIds[0];
  const isDiagram = variant === 'diagram';

  useEffect(() => {
    if (addOpen) setTimeout(() => addFormRef.current?.focus(), 50);
  }, [addOpen]);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    const loader = type === 'goals' ? listGoals(workspaceId) : listObjectives(workspaceId);
    loader.then((data) => {
      const arr = data as Array<Goal | StrategicObjective>;
      setAllItems(arr);
      setItems(arr.filter((i) => tacticItemBelongsOnCanvas(i, linkedStrategyIds)));
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, linkedStrategyIds.join(','), type]);

  const shownIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allItems
      .filter(
        (i) =>
          !shownIds.has(i.id) &&
          tacticItemBelongsOnCanvas(i, linkedStrategyIds) &&
          i.name.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [query, allItems, shownIds, linkedStrategyIds]);

  function nameExists(name: string): boolean {
    const t = name.trim().toLowerCase();
    return allItems.some((i) => i.name.trim().toLowerCase() === t);
  }

  async function handleCreate() {
    const name = query.trim();
    if (!name || !userId || !workspaceId) return;
    if (nameExists(name)) {
      setCreateErr(`A ${type === 'goals' ? 'goal' : 'objective'} with this name already exists.`);
      return;
    }
    if (createConfirm) {
      const decision = await createConfirm.requestConfirm({
        title: type === 'goals' ? 'Create goal' : 'Create objective',
        summary: `You are creating ${type === 'goals' ? 'goal' : 'objective'} "${name}". The list shows that new item plus the strategy and canvas relationships included together.`,
        rows: buildPreviewForObjectiveOrGoal({
          kind: type === 'goals' ? 'goal' : 'objective',
          name,
          canvasTitle,
          parentStrategyName: primaryStrategyName,
          linkedStrategyNamesOnCanvas,
        }),
      });
      if (decision !== 'proceed') return;
    }
    setCreateErr(null);
    setCreating(true);
    try {
      let created: Goal | StrategicObjective;
      if (type === 'goals') {
        created = await createGoal({
          name,
          description: '',
          timeHorizon: 'annual',
          priority: 'medium',
          status: 'active',
          workspaceId,
          strategyId: primaryStrategyId || undefined,
          metricName: null,
          targetValue: null,
          currentValue: null,
          unit: null,
        }, userId);
      } else {
        created = await createObjective({
          name,
          description: '',
          timeHorizon: 'annual',
          priority: 'medium',
          status: 'active',
          workspaceId,
          strategyId: primaryStrategyId || undefined,
        }, userId);
      }
      setItems((prev) => [...prev, created]);
      setAllItems((prev) => [...prev, created]);
      setQuery('');
      setAddOpen(false);
    } catch (e) {
      console.error('[TacticsSubCell] create error:', e);
      setCreateErr(e instanceof Error ? e.message : 'Could not create');
    }
    setCreating(false);
  }

  const listHref = type === 'objectives' ? '/prioritization/strategy/objectives' : '/prioritization/strategy/goals';
  const createLabel = type === 'goals' ? 'Create goal' : 'Create objective';
  const placeholder =
    type === 'goals' ? 'Search goals or type a new name…' : 'Search objectives or type a new name…';
  const Icon = type === 'goals' ? Target : Crosshair;
  const diagramCard =
    type === 'goals'
      ? 'border-[var(--green)]/70 dark:border-[var(--green)]/45'
      : 'border-[var(--slate)]/70 dark:border-[var(--slate)]/45';
  const diagramBtn =
    type === 'goals'
      ? 'border-[var(--green)] dark:border-[var(--green)]/60 bg-[var(--green-dim)] dark:bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/35 focus:ring-teal-500/60'
      : 'border-[var(--slate)] dark:border-[var(--slate)]/60 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/35 focus:ring-indigo-500/60';
  const iconCls =
    type === 'goals'
      ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
      : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]';

  const addEntityLabel = type === 'goals' ? 'Add goal' : 'Add objective';

  if (isDiagram) {
    return (
      <div className={` border bg-[var(--bg-raised)] p-4 shadow-sm ${diagramCard}`}>
        {loading && <p className="text-sm text-[var(--text-ter)] italic mb-2">Loading…</p>}

        {items.length > 0 ? (
          <ul className="space-y-1 mb-3 max-h-56 overflow-y-auto pr-1">
            {items.slice(0, 16).map((item) => (
              <li key={item.id}>
                <Link
                  to={listHref}
                  className={`flex items-start gap-2 text-sm text-[var(--text-pri)] hover:opacity-90 ${type === 'goals' ? 'hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]' : 'hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)]'}`}
                >
                  <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconCls}`} />
                  <span className="leading-snug">{item.name}</span>
                </Link>
              </li>
            ))}
            {items.length > 16 && (
              <li className="text-xs text-[var(--text-ter)] pl-9">+{items.length - 16} more</li>
            )}
          </ul>
        ) : (
          <p className="text-sm text-[var(--text-ter)] mb-3 italic">
            No {type === 'goals' ? 'goals' : 'objectives'} here yet. Click + to search or create one.
          </p>
        )}

        {!addOpen ? (
          <button
            type="button"
            onClick={() => {
              setAddOpen(true);
              setCreateErr(null);
            }}
            className={`${CANVAS_ADD_ICON_BTN_LAYOUT} ${type === 'goals' ? 'border-[var(--green)]/80 bg-[var(--bg-raised)] text-[var(--green)] hover:bg-[var(--green-dim)] dark:border-[var(--green)]/45 dark:text-[var(--text-ter)] dark:hover:bg-[var(--green)]/35 dark:hover:text-[var(--text-ter)]' : 'border-[var(--slate)]/80 bg-[var(--bg-raised)] text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:border-[var(--slate)]/45 dark:text-[var(--text-ter)] dark:hover:bg-[var(--slate)]/35 dark:hover:text-[var(--text-ter)]'}`}
            title={addEntityLabel}
            aria-label={addEntityLabel}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
          </button>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-start">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                <input
                  ref={addFormRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setCreateErr(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    if (suggestions.length > 0) {
                      setItems((prev) => [...prev, suggestions[0]]);
                      setQuery('');
                      setCreateErr(null);
                    } else if (query.trim()) {
                      void handleCreate();
                    }
                  }}
                  placeholder={placeholder}
                  disabled={creating}
                  className={`w-full py-2 pl-10 pr-3 text-sm border border-[var(--rule)]  bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-2 ${type === 'goals' ? 'focus:ring-teal-500/60' : 'focus:ring-indigo-500/60'} disabled:opacity-50`}
                />
              </div>
              <button
                type="button"
                onClick={() => void handleCreate()}
                disabled={creating || !query.trim()}
                className={`inline-flex items-center justify-center gap-1  px-3 py-2 text-sm font-medium disabled:opacity-40 disabled:pointer-events-none whitespace-nowrap shrink-0 focus:outline-none focus:ring-2 ${diagramBtn}`}
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {creating ? 'Adding…' : createLabel}
              </button>
            </div>

            {createErr && <p className="mt-2 text-sm text-[var(--coral)] dark:text-[var(--coral)]">{createErr}</p>}

            {suggestions.length > 0 && (
              <div className="mt-2 border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] shadow-sm max-h-48 overflow-y-auto z-30 relative">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setItems((prev) => [...prev, s]);
                      setQuery('');
                      setCreateErr(null);
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm text-[var(--text-pri)] border-b last:border-b-0 border-[var(--rule)] transition-colors ${type === 'goals' ? 'hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/30' : 'hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/30'}`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}

            {query.trim() && suggestions.length === 0 && !creating && !nameExists(query) && (
              <p className="mt-2 text-xs text-[var(--text-sec)]">
                Press Enter or {createLabel} to add &quot;{query.trim()}&quot;
                {primaryStrategyId ? ' (scoped to the first strategy on this canvas).' : ''}
              </p>
            )}

            <button
              type="button"
              onClick={() => {
                setAddOpen(false);
                setQuery('');
                setCreateErr(null);
              }}
              className="mt-2 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
            >
              Cancel
            </button>
          </>
        )}

        <p className="mt-3 pt-2 border-t border-[var(--rule)] dark:border-[var(--rule)]/80">
          <Link
            to={listHref}
            className="text-[10px] text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
          >
            {type === 'goals' ? 'All goals' : 'All objectives'}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div>
      {loading && <p className="text-xs text-[var(--text-ter)] italic">Loading…</p>}

      {items.length > 0 && (
        <ul className="space-y-1 mb-2">
          {items.slice(0, 5).map((item) => (
            <li key={item.id} className="text-sm text-[var(--text-pri)] leading-snug">
              · {item.name}
            </li>
          ))}
          {items.length > 5 && (
            <li className="text-xs text-[var(--text-ter)] italic">+{items.length - 5} more</li>
          )}
        </ul>
      )}

      {items.length === 0 && !loading && (
        <p className="text-xs text-[var(--text-ter)] italic mb-2">
          No {type === 'goals' ? 'goals' : 'objectives'} here yet. Click + to add one.
        </p>
      )}

      {!addOpen ? (
        <button
          type="button"
          onClick={() => {
            setAddOpen(true);
            setCreateErr(null);
          }}
          className={`${CANVAS_ADD_ICON_BTN_LAYOUT} border-[var(--rule)]/90 bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)] hover:text-[var(--text-pri)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-white`}
          title={addEntityLabel}
          aria-label={addEntityLabel}
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      ) : (
        <>
          <div className="relative">
            <input
              ref={addFormRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setCreateErr(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (suggestions.length > 0) {
                    setItems((prev) => [...prev, suggestions[0]]);
                    setQuery('');
                  } else if (query.trim()) {
                    void handleCreate();
                  }
                }
                if (e.key === 'Escape') setQuery('');
              }}
              placeholder={`+ ${type === 'goals' ? 'Goal' : 'Objective'}…`}
              disabled={creating}
              className="w-full text-sm placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] bg-[var(--bg-raised)]/90 dark:bg-gray-950/90 border border-[var(--rule)] px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-40"
            />
          </div>

          {createErr && <p className="text-xs text-[var(--coral)] mt-1">{createErr}</p>}

          {suggestions.length > 0 && (
            <div className="mt-1 border border-[var(--rule)] bg-[var(--bg-raised)] shadow-sm z-20 relative">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { setItems((prev) => [...prev, s]); setQuery(''); }}
                  className="w-full text-left px-2.5 py-2 text-sm text-[var(--text-pri)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 border-b last:border-b-0 border-[var(--rule)]"
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}

          {query.trim() && suggestions.length === 0 && !creating && !nameExists(query) && (
            <p className="text-xs text-[var(--text-sec)] mt-1.5">
              Press Enter to create &quot;{query.trim()}&quot;
              {primaryStrategyId ? ' under the first linked strategy.' : ' (unassigned until you link a strategy).'}
            </p>
          )}
          {creating && <p className="text-xs text-[var(--text-ter)] mt-1.5">Creating…</p>}

          <button
            type="button"
            onClick={() => {
              setAddOpen(false);
              setQuery('');
              setCreateErr(null);
            }}
            className="mt-2 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
          >
            Cancel
          </button>
        </>
      )}
    </div>
  );
}

function DiagramView({
  canvas,
  linkedStrategies,
  allStrategies,
  onSave,
  onLinkStrategy,
  onUnlinkStrategy,
  workspaceId,
  userId,
}: {
  canvas: StrategyCanvas;
  linkedStrategies: Strategy[];
  allStrategies: Strategy[];
  onSave: (key: keyof UpdateCanvasInput, val: string) => void;
  onLinkStrategy: (strategyId: string) => Promise<void> | void;
  onUnlinkStrategy: (strategyId: string) => void;
  workspaceId: string;
  userId: string;
}) {
  return (
    <div className="overflow-x-auto overflow-y-visible border border-[var(--rule)] bg-[var(--hover-tint)]/80 dark:bg-gray-950/50 py-3 sm:py-5 px-1 sm:px-3 lg:px-5 -mx-2 sm:-mx-3">
      <div className="min-w-[720px] w-full max-w-[1800px] mx-auto text-sm bg-[var(--bg-raised)] border-2 border-[var(--rule)] shadow-sm overflow-hidden">
        {/* Canvas title header */}
        <div className="border-b-2 border-[var(--rule)] py-3 px-4 text-center bg-[var(--surface)]/90 dark:bg-[var(--bg)]/80">
          <span className="text-xs sm:text-sm font-bold tracking-[0.25em] uppercase text-[var(--text-sec)]">
            Strategy Canvas
          </span>
          {canvas.title && canvas.title !== 'Untitled Canvas' && (
            <span className="text-xs sm:text-sm text-[var(--text-sec)] ml-2">— {canvas.title}</span>
          )}
        </div>

        {/* ── Row 1: DRIVERS ──────────────────────────────────── */}
        <div className="border-b-2 border-[var(--rule)] px-4 sm:px-6 py-5 min-h-[100px] bg-[var(--bg-raised)]">
          <CanvasCellLabel text="Drivers" />
          <DiagramOutcomeDriversPicker
            linkedStrategyIds={linkedStrategies.map((s) => s.id)}
            canvasTitle={canvas.title}
            primaryStrategyName={linkedStrategies[0]?.name ?? null}
            linkedStrategyNamesOnCanvas={linkedStrategies.map((s) => s.name)}
          />
          {/* Assumptions — editable */}
          <div className="border-t border-[var(--rule)] pt-4 mt-2 bg-[var(--surface)]/50 dark:bg-[var(--bg)]/30 -mx-4 sm:-mx-6 px-4 sm:px-6 pb-2 rounded-b-none">
            <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-[var(--text-sec)] mb-2">
              Assumptions
            </div>
            <DiagramEditableCell
              fieldKey="assumptions"
              value={canvas.assumptions}
              placeholder="What are we taking for granted about the environment?"
              onSave={onSave}
            />
          </div>
        </div>

        {/* Arrows: DRIVERS → PURPOSE / VISION / MISSION */}
        <DownArrowRow weights={[1, 1, 1]} />

        {/* ── Row 2: PURPOSE | VISION | MISSION ──────────────── */}
        <div
          className="grid border-b-2 border-[var(--rule)] bg-[var(--bg-raised)]"
          style={{ gridTemplateColumns: '1fr 1fr 1fr' }}
        >
          <div className="px-4 sm:px-6 py-5 min-h-[132px] lg:min-h-[148px]">
            <CanvasCellLabel text="Purpose" />
            <DiagramEditableCell fieldKey="purpose" value={canvas.purpose} placeholder="Why do we exist?" onSave={onSave} />
          </div>
          <div className="px-4 sm:px-6 py-5 min-h-[132px] lg:min-h-[148px] border-x-2 border-[var(--rule)] bg-[var(--surface)]/40 dark:bg-[var(--bg)]/25">
            <CanvasCellLabel text="Vision" />
            <DiagramEditableCell fieldKey="vision" value={canvas.vision} placeholder="Where are we going?" onSave={onSave} />
          </div>
          <div className="px-4 sm:px-6 py-5 min-h-[132px] lg:min-h-[148px]">
            <CanvasCellLabel text="Mission" />
            <DiagramEditableCell fieldKey="mission" value={canvas.mission} placeholder="What do we do?" onSave={onSave} />
          </div>
        </div>

        {/* Arrows: row 2 → row 3 */}
        <DownArrowRow weights={[1, 1, 1]} />

        {/* ── Row 3: GUIDING PRINCIPLES | STRATEGY ───────────── */}
        <div
          className="relative border-b-2 border-[var(--rule)] bg-[var(--bg-raised)]"
          style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}
        >
          <div className="px-4 sm:px-6 py-5 min-h-[150px] lg:min-h-[170px]">
            <CanvasCellLabel text="Guiding Principles" />
            <DiagramEditableCell fieldKey="guidingPrinciples" value={canvas.guidingPrinciples} placeholder="How do we behave?" onSave={onSave} />
          </div>
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10" style={{ left: 'calc(100% / 3)' }}>
            <BidirArrow />
          </div>
          <div className="px-4 sm:px-6 py-5 min-h-[160px] lg:min-h-[200px] border-l-2 border-[var(--rule)] bg-[var(--slate-dim)]/35 dark:bg-[var(--slate)]/15">
            <CanvasCellLabel text="Strategy" />
            <StrategyLinkPicker
              variant="diagram"
              canvasTitle={canvas.title}
              linkedStrategyIds={canvas.linkedStrategyIds}
              allStrategies={allStrategies}
              onLink={onLinkStrategy}
              onUnlink={onUnlinkStrategy}
              userId={userId}
              workspaceId={workspaceId}
            />
          </div>
        </div>

        {/* Arrows: row 3 → row 4 */}
        <DownArrowRow weights={[1, 2]} />

        {/* ── Row 4: CORE COMPETENCIES | TACTICS ─────────────── */}
        <div
          className="relative bg-[var(--bg-raised)]"
          style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}
        >
          <div className="px-4 sm:px-6 py-5 min-h-[200px] lg:min-h-[220px]">
            <CanvasCellLabel text="Core Competencies" />
            <DiagramEditableCell fieldKey="competencies" value={canvas.competencies} placeholder="What are we uniquely good at?" onSave={onSave} />
          </div>
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10" style={{ left: 'calc(100% / 3)' }}>
            <BidirArrow />
          </div>
          <div className="border-l-2 border-[var(--rule)] min-h-[200px] lg:min-h-[240px] bg-[var(--surface)]/30 dark:bg-[var(--bg)]/20">
            <div className="px-4 sm:px-6 pt-5 pb-1">
              <CanvasCellLabel text="Objectives and Goals" />
            </div>
            <div
              className="grid border-t border-[var(--rule)] bg-[var(--bg-raised)]"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <div className="px-3 sm:px-5 py-4">
                <CanvasCellLabel text="Objectives" />
                <TacticsSubCell
                  variant="diagram"
                  type="objectives"
                  linkedStrategyIds={linkedStrategies.map((s) => s.id)}
                  canvasTitle={canvas.title}
                  primaryStrategyName={linkedStrategies[0]?.name ?? null}
                  linkedStrategyNamesOnCanvas={linkedStrategies.map((s) => s.name)}
                  workspaceId={workspaceId}
                  userId={userId}
                />
              </div>
              <div className="px-3 sm:px-5 py-4 border-l border-[var(--rule)]">
                <CanvasCellLabel text="Goals" />
                <TacticsSubCell
                  variant="diagram"
                  type="goals"
                  linkedStrategyIds={linkedStrategies.map((s) => s.id)}
                  canvasTitle={canvas.title}
                  primaryStrategyName={linkedStrategies[0]?.name ?? null}
                  linkedStrategyNamesOnCanvas={linkedStrategies.map((s) => s.name)}
                  workspaceId={workspaceId}
                  userId={userId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────

export function StrategyCanvasEditor() {
  const { canvasId } = useParams<{ canvasId: string }>();
  const navigate = useNavigate();

  const canvases      = useStrategyCanvasStore((s) => s.canvases);
  const fetchCanvases = useStrategyCanvasStore((s) => s.fetchCanvases);
  const updateCanvas  = useStrategyCanvasStore((s) => s.updateCanvas);
  const linkStrategy  = useStrategyCanvasStore((s) => s.linkStrategy);
  const unlinkStrategy = useStrategyCanvasStore((s) => s.unlinkStrategy);
  const workspaceId   = useTenantStore((s) => s.currentWorkspaceId);

  const strategiesMap     = useStrategyStore((s) => s.strategies);
  const fetchStrategies   = useStrategyStore((s) => s.fetchFromSupabase);
  const userId            = useUserStore((s) => s.userId);

  const [saving, setSaving] = useState<keyof UpdateCanvasInput | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [viewMode, setViewMode] = useState<'components' | 'diagram'>('diagram');
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (workspaceId) {
      fetchCanvases();
      fetchStrategies();
    }
  }, [workspaceId, fetchCanvases, fetchStrategies]);

  const canvas: StrategyCanvas | undefined = canvases.find((c) => c.id === canvasId);

  useEffect(() => {
    if (!canvas) return;
    setTitleDraft(canvas.title);
    // Intentionally not depending on `canvas` object identity (store churn).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow server title when this record’s id/title change
  }, [canvas?.id, canvas?.title]);

  const allStrategies = useMemo(() => {
    const all = strategiesMap instanceof Map
      ? Array.from(strategiesMap.values())
      : Object.values(strategiesMap ?? {});
    return all.filter((s) => s.status !== 'archived') as Strategy[];
  }, [strategiesMap]);

  const linkedForCanvasStrategies = useMemo(() => {
    if (!canvas) return [] as Strategy[];
    return allStrategies.filter((s) => canvas.linkedStrategyIds.includes(s.id));
  }, [canvas, allStrategies]);

  const [unlinkModal, setUnlinkModal] = useState<StrategyUnlinkModalState>({ open: false });
  const [unlinkBusy, setUnlinkBusy] = useState(false);

  const beginUnlinkStrategy = useCallback(
    async (strategyId: string) => {
      if (!canvasId || !workspaceId) return;
      const c = canvases.find((x) => x.id === canvasId);
      if (!c) return;
      const strat = allStrategies.find((x) => x.id === strategyId);
      const strategyName = strat?.name ?? 'This strategy';
      setUnlinkModal({ open: true, loading: true, strategyId, strategyName });
      try {
        await useOutcomeDriverStore.getState().fetchFromSupabase();
        const driversAll = useOutcomeDriverStore.getState().drivers;
        const [goalsRaw, objectivesRaw] = await Promise.all([
          listGoals(workspaceId),
          listObjectives(workspaceId),
        ]);
        const objectives = objectivesRaw
          .filter((o) => o.strategyId === strategyId)
          .map((o) => o.name);
        const goals = goalsRaw
          .filter((g) => g.strategyId === strategyId)
          .map((g) => g.name);
        const outcomeDrivers = driversAll
          .filter((d) => d.strategyId === strategyId)
          .map((d) => (d.title?.trim() ? d.title : 'Untitled driver'));
        const otherLinkedStrategyNames = allStrategies
          .filter((s) => c.linkedStrategyIds.includes(s.id) && s.id !== strategyId)
          .map((s) => s.name);
        setUnlinkModal({
          open: true,
          loading: false,
          strategyId,
          strategyName,
          impact: {
            objectives,
            goals,
            outcomeDrivers,
            otherLinkedStrategyNames,
          },
        });
      } catch (e) {
        console.error('[StrategyCanvasEditor] beginUnlinkStrategy:', e);
        setUnlinkModal({ open: false });
      }
    },
    [canvasId, workspaceId, canvases, allStrategies],
  );

  const confirmUnlinkStrategy = useCallback(async () => {
    if (!canvasId || !unlinkModal.open || unlinkModal.loading) return;
    const { strategyId } = unlinkModal;
    setUnlinkBusy(true);
    await unlinkStrategy(canvasId, strategyId);
    setUnlinkBusy(false);
    setUnlinkModal({ open: false });
  }, [canvasId, unlinkModal, unlinkStrategy]);

  const handleBlockSave = useCallback(
    async (key: keyof UpdateCanvasInput, value: string) => {
      if (!canvasId) return;
      setSaving(key);
      setSaveError(null);
      const { error } = await updateCanvas(canvasId, { [key]: value || null });
      setSaving(null);
      if (error) setSaveError(error);
    },
    [canvasId, updateCanvas]
  );

  async function commitTitle() {
    if (!canvasId || !canvas) return;
    setTitleEditing(false);
    const trimmed = titleDraft.trim() || 'Untitled Canvas';
    if (trimmed !== canvas.title) {
      await updateCanvas(canvasId, { title: trimmed });
    }
  }

  if (!canvas) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
      </div>
    );
  }

  return (
    <StrategyCanvasCreateConfirmProvider onSwitchToComponents={() => setViewMode('components')}>
      <div className={`space-y-6 ${viewMode === 'diagram' ? 'w-full max-w-none' : ''}`}>
      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => navigate('/prioritization/strategy/canvas')}
          className="mt-1 p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <PageHeader
            icon={<LayoutTemplate className="h-4 w-4 text-[var(--text-ter)]" />}
            iconBg="bg-[var(--slate-dim)]"
            title={
              titleEditing ? (
                <input
                  ref={titleInputRef}
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={commitTitle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitTitle();
                    if (e.key === 'Escape') { setTitleEditing(false); setTitleDraft(canvas.title); }
                  }}
                  className="bg-transparent border-b border-[var(--slate)] outline-none text-xl font-bold w-full"
                  autoFocus
                />
              ) : (
                <span
                  className="cursor-pointer hover:text-[var(--text-ter)] transition-colors"
                  onClick={() => { setTitleEditing(true); setTimeout(() => titleInputRef.current?.focus(), 50); }}
                >
                  {canvas.title}
                </span>
              )
            }
            subtitle={`Strategy Canvas · last updated ${new Date(canvas.updatedAt).toLocaleDateString()}`}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/prioritization/strategy/canvas/${canvasId}/print`}
                  className="inline-flex items-center gap-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] shadow-sm hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] print:hidden"
                  title="Paper-sized print preview"
                >
                  <Printer className="h-3.5 w-3.5" />
                  Print
                </Link>
                <StrategyConnectivityHelpButton context="canvas_editor" />
              </div>
            }
          />
        </div>
      </div>

      {/* View toggle + save error row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* View mode toggle */}
        <div className="inline-flex items-center border border-[var(--rule)] bg-[var(--bg-raised)] p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('diagram')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5  text-xs font-medium transition-colors ${
              viewMode === 'diagram'
                ? 'bg-[var(--slate)] text-white shadow-sm'
                : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]'
            }`}
          >
            <Network className="h-3.5 w-3.5" />
            Diagram
          </button>
          <button
            type="button"
            onClick={() => setViewMode('components')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5  text-xs font-medium transition-colors ${
              viewMode === 'components'
                ? 'bg-[var(--slate)] text-white shadow-sm'
                : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]'
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Components
          </button>
        </div>

        {saveError && (
          <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] text-[var(--coral)] text-sm flex-1">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {saveError}
          </div>
        )}
      </div>

      {/* Diagram view */}
      {viewMode === 'diagram' && (
        <DiagramView
          canvas={canvas}
          linkedStrategies={linkedForCanvasStrategies}
          allStrategies={allStrategies}
          onSave={handleBlockSave}
          onLinkStrategy={async (strategyId) => {
            await linkStrategy(canvas.id, strategyId);
          }}
          onUnlinkStrategy={(strategyId) => {
            void beginUnlinkStrategy(strategyId);
          }}
          workspaceId={workspaceId ?? ''}
          userId={userId ?? ''}
        />
      )}

      {/* Main grid layout */}
      {viewMode === 'components' && <div className="flex gap-6 items-start">

        {/* Left: canvas components */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* Row 1: Vision + Purpose + Mission */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {BLOCKS.slice(0, 3).map((block) => (
              <CanvasBlock
                key={block.key}
                block={block}
                value={(canvas[block.key as keyof StrategyCanvas] as string | null) ?? null}
                onSave={handleBlockSave}
                saving={saving === block.key}
              />
            ))}
          </div>

          {/* Row 2: Guiding Principles + Competencies + Assumptions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {BLOCKS.slice(3).map((block) => (
              <CanvasBlock
                key={block.key}
                block={block}
                value={(canvas[block.key as keyof StrategyCanvas] as string | null) ?? null}
                onSave={handleBlockSave}
                saving={saving === block.key}
              />
            ))}
          </div>

          {/* Row 3: Strategy */}
          <div className="border-2 border-[var(--slate)] dark:border-[var(--slate)]/40 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/10 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--slate)] dark:border-[var(--slate)]/40">
              <BarChart3 className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-sec)]">
                Strategy
              </span>
            </div>
            <div className="px-4 py-3">
              <LinkedStrategiesPanel
                canvasTitle={canvas.title}
                linkedStrategyIds={canvas.linkedStrategyIds}
                allStrategies={allStrategies}
                onLink={async (strategyId) => {
                  await linkStrategy(canvas.id, strategyId);
                }}
                onUnlink={(strategyId) => {
                  void beginUnlinkStrategy(strategyId);
                }}
                userId={userId ?? ''}
                workspaceId={workspaceId ?? ''}
              />
            </div>
          </div>

          {/* Row 4: Objectives and Goals */}
          <div className="border-2 border-[var(--slate)] dark:border-[var(--slate)]/40 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/10 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--slate)] dark:border-[var(--slate)]/40">
              <Target className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-sec)]">
                Objectives and Goals
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-indigo-100 dark:divide-indigo-800/40 px-0">
              <div className="px-4 py-3">
                <p className="text-xs font-medium text-[var(--text-sec)] mb-2">Objectives</p>
                <TacticsSubCell
                  type="objectives"
                  linkedStrategyIds={canvas.linkedStrategyIds}
                  canvasTitle={canvas.title}
                  primaryStrategyName={linkedForCanvasStrategies[0]?.name ?? null}
                  linkedStrategyNamesOnCanvas={linkedForCanvasStrategies.map((s) => s.name)}
                  workspaceId={workspaceId ?? ''}
                  userId={userId}
                />
                <Link
                  to="/prioritization/strategy/objectives"
                  className="inline-flex items-center gap-1 text-[10px] text-[var(--text-sec)] hover:text-[var(--text-sec)] mt-2"
                >
                  <Crosshair className="h-3 w-3 opacity-70" />
                  All objectives
                </Link>
              </div>
              <div className="px-4 py-3">
                <p className="text-xs font-medium text-[var(--text-sec)] mb-2">Goals</p>
                <TacticsSubCell
                  type="goals"
                  linkedStrategyIds={canvas.linkedStrategyIds}
                  canvasTitle={canvas.title}
                  primaryStrategyName={linkedForCanvasStrategies[0]?.name ?? null}
                  linkedStrategyNamesOnCanvas={linkedForCanvasStrategies.map((s) => s.name)}
                  workspaceId={workspaceId ?? ''}
                  userId={userId}
                />
                <Link
                  to="/prioritization/strategy/goals"
                  className="inline-flex items-center gap-1 text-[10px] text-[var(--text-sec)] hover:text-[var(--text-sec)] mt-2"
                >
                  <Target className="h-3 w-3 opacity-70" />
                  All goals
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Right: sticky panels */}
        <div className="w-64 flex-shrink-0 sticky top-6 space-y-4">

          {/* Outcome Drivers */}
          <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)]">
              <TreeDeciduous className="h-3.5 w-3.5 text-[var(--green)]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-sec)]">
                Outcome Drivers
              </span>
            </div>
            <div className="px-3 py-3">
              <DriversPanel
                canvasTitle={canvas.title}
                linkedStrategyIds={canvas.linkedStrategyIds}
                allStrategies={allStrategies}
              />
              <div className="mt-3 pt-3 border-t border-[var(--rule)]">
                <Link
                  to="/prioritization/outcome-drivers"
                  className="text-[10px] text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
                >
                  All outcome drivers
                </Link>
              </div>
            </div>
          </div>

          {/* About */}
          <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
            <h4 className="text-xs font-semibold text-[var(--text-sec)] mb-2">About this canvas</h4>
            <p className="text-xs text-[var(--text-ter)] leading-relaxed">
              This one-page framework lays out the essential components that put strategy in context
              and help people make sense of complex change.
            </p>
            <p className="text-xs text-[var(--text-ter)] leading-relaxed mt-2">
              Click any component to edit it inline. Link strategies in the panel above to connect
              this canvas to your active strategic direction.
            </p>
          </div>
        </div>
      </div>}
    </div>
    <StrategyCanvasUnlinkConfirmModal
      open={unlinkModal.open}
      loading={unlinkModal.open && unlinkModal.loading}
      strategyName={unlinkModal.open ? unlinkModal.strategyName : ''}
      impact={unlinkModal.open && !unlinkModal.loading ? unlinkModal.impact : null}
      busy={unlinkBusy}
      onCancel={() => {
        if (!unlinkBusy) setUnlinkModal({ open: false });
      }}
      onConfirm={() => void confirmUnlinkStrategy()}
    />
    </StrategyCanvasCreateConfirmProvider>
  );
}

export default StrategyCanvasEditor;
